import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ChevronRight, ArrowLeft, HelpCircle } from 'lucide-react';

// FAQ Database
const FAQ_CATEGORIES = [
  {
    id: 'shipping',
    icon: '📦',
    title: 'Envíos',
    questions: [
      {
        q: '¿Cuánto tarda en llegar mi pedido?',
        a: 'Los pedidos se entregan en 24-72 horas según el transportista:\n\n• SEUR: 24-48 horas\n• Correos Express: 24-48 horas\n• InPost: 48-72 horas\n\nLos pedidos realizados antes de las 14:00 se procesan el mismo día.'
      },
      {
        q: '¿Cuánto cuesta el envío?',
        a: '¡Envío GRATIS en pedidos superiores a 80€! 🎉\n\nPara pedidos menores:\n• SEUR: 5.99€\n• Correos Express: 4.99€\n• InPost (punto de recogida): 3.49€'
      },
      {
        q: '¿Hacéis envíos a Canarias/Baleares?',
        a: 'Por el momento solo realizamos envíos a España Peninsular. Estamos trabajando para ampliar nuestras zonas de envío pronto.'
      },
      {
        q: '¿Cómo puedo seguir mi pedido?',
        a: 'Puedes seguir tu pedido en nuestra página de seguimiento:\n\n1. Ve a "Seguir mi pedido" en el footer\n2. Introduce tu número de pedido\n3. Verás el estado actualizado\n\nTambién recibirás un email con el número de seguimiento cuando se envíe.'
      }
    ]
  },
  {
    id: 'returns',
    icon: '↩️',
    title: 'Devoluciones',
    questions: [
      {
        q: '¿Puedo devolver un producto?',
        a: 'Sí, tienes **15 días** desde la recepción para devolver cualquier producto sin usar y con etiquetas.\n\n**Opciones disponibles:**\n\n✅ **Cambio de talla:** GRATIS\n✅ **Tarjeta regalo:** GRATIS (por el valor total)\n✅ **Reembolso:** Con 15% de retención por gastos de gestión\n\nContacta con nosotros por WhatsApp o email para iniciar el proceso.'
      },
      {
        q: '¿Cómo hago un cambio de talla?',
        a: 'Para cambios de talla:\n\n1. Contacta con nosotros por WhatsApp o email\n2. Indica tu número de pedido y la talla que necesitas\n3. Te enviamos etiqueta de envío gratuita\n4. Una vez recibido, enviamos la nueva talla\n\n¡Los cambios de talla son **100% gratuitos**!'
      },
      {
        q: '¿Cómo funciona la devolución con reembolso?',
        a: 'Si prefieres que te devolvamos el dinero:\n\n1. Contacta con nosotros (15 días máximo)\n2. Te enviamos etiqueta de devolución\n3. Una vez recibido el producto, procesamos el reembolso\n\n⚠️ **Importante:** Se retiene un 15% del importe por gastos de gestión y envío.\n\nEl reembolso se procesa en 5-7 días laborables.'
      },
      {
        q: '¿Puedo cambiar por una tarjeta regalo?',
        a: '¡Sí! Es la opción más popular 🎁\n\nSi el producto no te convence, puedes cambiarlo por una **Tarjeta Regalo** del **valor total** de tu compra (sin retención).\n\n• La tarjeta no caduca\n• Puedes usarla en cualquier producto\n• Se envía por email en 24-48h\n\n¡Así no pierdes nada y eliges con calma!'
      }
    ]
  },
  {
    id: 'sizes',
    icon: '📏',
    title: 'Tallas',
    questions: [
      {
        q: '¿Cómo sé qué talla elegir?',
        a: 'Nuestra ropa tiene un ajuste deportivo. Te recomendamos:\n\n• Si estás entre dos tallas, elige la mayor\n• Los leggings tienen efecto compresión, elige tu talla habitual\n• Los tops son de corte ajustado\n\n¿Dudas? Escríbenos por WhatsApp con tus medidas.'
      },
      {
        q: '¿Qué tallas tenéis disponibles?',
        a: 'Disponemos de tallas XS a XL en la mayoría de productos:\n\n• XS: 34-36\n• S: 36-38\n• M: 38-40\n• L: 40-42\n• XL: 42-44'
      },
      {
        q: '¿La ropa encoge al lavarla?',
        a: 'Nuestras prendas están pre-encogidas y mantienen su forma. Para mejor cuidado:\n\n• Lavar a máximo 30°C\n• No usar secadora\n• No planchar directamente sobre estampados'
      }
    ]
  },
  {
    id: 'payment',
    icon: '💳',
    title: 'Pagos',
    questions: [
      {
        q: '¿Qué métodos de pago aceptáis?',
        a: 'Aceptamos:\n\n• 💳 Tarjeta de crédito/débito (Visa, Mastercard)\n• 🅿️ PayPal\n\nTodos los pagos son 100% seguros con cifrado SSL.'
      },
      {
        q: '¿Puedo pagar a plazos?',
        a: 'Por el momento no ofrecemos pago a plazos, pero estamos trabajando en añadir esta opción pronto.'
      },
      {
        q: '¿Es seguro pagar en vuestra web?',
        a: '¡Absolutamente! 🔒\n\n• Usamos cifrado SSL\n• Procesamos pagos a través de Stripe y PayPal\n• Nunca almacenamos datos de tu tarjeta\n• Cumplimos con la normativa PCI-DSS'
      }
    ]
  },
  {
    id: 'products',
    icon: '👕',
    title: 'Productos',
    questions: [
      {
        q: '¿De qué materiales está hecha la ropa?',
        a: 'Usamos materiales de alta calidad:\n\n• Poliéster reciclado: transpirable y sostenible\n• Elastano: para flexibilidad\n• Nylon: resistente y duradero\n\nTodas nuestras prendas son transpirables, de secado rápido y con protección UV.'
      },
      {
        q: '¿La ropa es apta para yoga/crossfit/running?',
        a: '¡Sí! Nuestra Collection Lotus está diseñada para:\n\n• Yoga y pilates\n• Entrenamiento en gimnasio\n• Running y cardio\n• CrossFit\n• Uso diario\n\nMateriales flexibles con compresión moderada.'
      },
      {
        q: '¿Cuándo volvéis a tener stock?',
        a: 'Reponemos stock cada 2-3 semanas. Si un producto está agotado:\n\n1. Escríbenos por WhatsApp\n2. Te avisamos cuando vuelva a estar disponible\n\n¡También puedes seguirnos en Instagram para novedades!'
      }
    ]
  },
  {
    id: 'contact',
    icon: '📞',
    title: 'Contacto',
    questions: [
      {
        q: '¿Cómo puedo contactar con vosotros?',
        a: 'Puedes contactarnos por:\n\n📧 Email: infojrfsportswear@gmail.com\n📱 WhatsApp: +34 611 871 722\n\nHorario de atención: Lunes a Viernes, 9:00 - 18:00'
      },
      {
        q: '¿Tenéis tienda física?',
        a: 'Somos una tienda 100% online, lo que nos permite ofrecer los mejores precios.\n\n¿Quieres ver los productos? Síguenos en Instagram para ver fotos reales de clientas con nuestras prendas.'
      }
    ]
  }
];

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [messages, setMessages] = useState([
    { 
      type: 'bot', 
      text: '¡Hola! 👋 Soy el asistente de JRF Sportswear. ¿En qué puedo ayudarte?' 
    }
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, selectedCategory, selectedQuestion]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedQuestion(null);
    setMessages(prev => [
      ...prev,
      { type: 'user', text: category.title },
      { type: 'bot', text: `Estas son las preguntas más frecuentes sobre ${category.title.toLowerCase()}:` }
    ]);
  };

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
    setMessages(prev => [
      ...prev,
      { type: 'user', text: question.q },
      { type: 'bot', text: question.a }
    ]);
  };

  const handleBack = () => {
    if (selectedQuestion) {
      setSelectedQuestion(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  const handleReset = () => {
    setSelectedCategory(null);
    setSelectedQuestion(null);
    setMessages([
      { 
        type: 'bot', 
        text: '¡Hola! 👋 Soy el asistente de JRF Sportswear. ¿En qué puedo ayudarte?' 
      }
    ]);
  };

  return (
    <>
      {/* Chat Bubble Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-28 right-6 z-[9999] w-14 h-14 rounded-full shadow-lg ring-2 ring-white flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-700 hover:bg-gray-600' 
            : 'bg-black hover:bg-neutral-800'
        }`}
        data-testid="chat-bubble-button"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Notification Badge */}
      {!isOpen && (
        <span className="fixed bottom-[176px] right-5 z-[9999] bg-red-500 pointer-events-none text-white text-xs px-2 py-1 rounded-full animate-pulse">
          ¿Ayuda?
        </span>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-44 right-6 z-[9999] w-80 sm:w-96 h-[480px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-black text-white p-4 flex items-center gap-3">
            {(selectedCategory || selectedQuestion) && (
              <button 
                onClick={handleBack}
                className="p-1 hover:bg-white/20 rounded-full transition"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex-1">
              <h3 className="font-bold">Asistente JRF</h3>
              <p className="text-xs text-white/80">Respuestas instantáneas 24/7</p>
            </div>
            <HelpCircle className="w-6 h-6 text-white/60" />
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-2xl ${
                    message.type === 'user'
                      ? 'bg-black text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-md border border-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-line">{message.text}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Options Area */}
          <div className="border-t bg-white p-3 max-h-48 overflow-y-auto">
            {!selectedCategory ? (
              // Show categories
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Selecciona un tema:</p>
                <div className="grid grid-cols-2 gap-2">
                  {FAQ_CATEGORIES.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryClick(category)}
                      className="flex items-center gap-2 p-2 text-left text-sm bg-gray-50 hover:bg-neutral-50 rounded-lg transition border border-gray-200 hover:border-neutral-300"
                      data-testid={`category-${category.id}`}
                    >
                      <span className="text-lg">{category.icon}</span>
                      <span className="font-medium text-gray-700">{category.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : !selectedQuestion ? (
              // Show questions for selected category
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Preguntas frecuentes:</p>
                {selectedCategory.questions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionClick(question)}
                    className="w-full flex items-center justify-between p-2 text-left text-sm bg-gray-50 hover:bg-neutral-50 rounded-lg transition border border-gray-200 hover:border-neutral-300"
                    data-testid={`question-${index}`}
                  >
                    <span className="text-gray-700 pr-2">{question.q}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : (
              // Show more options after answer
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">¿Te ha sido útil?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedQuestion(null)}
                    className="flex-1 p-2 text-sm bg-gray-50 hover:bg-neutral-50 rounded-lg transition border border-gray-200 hover:border-neutral-300"
                  >
                    Otra pregunta de {selectedCategory.title}
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex-1 p-2 text-sm bg-black text-white rounded-lg hover:opacity-90 transition"
                  >
                    Nuevo tema
                  </button>
                </div>
                <a
                  href="https://wa.me/34611871722"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full p-2 text-sm text-center bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                >
                  💬 Hablar con una persona
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBubble;
